import { ArrowDropDown, ArrowDropUp } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import { Chip, Menu, MenuItem } from '@workboard/wobo-ui';
import React, { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useTabs } from '../useTabs';

declare global {
  interface Window {
    myWorkActiveTab: any;
  }
}

const Wrapper = styled.div`
  display: inline-grid;
  grid-template-columns: repeat(8, auto);
  gap: 10px;
`;

const IconWrapper = styled.span<{ $selected?: boolean }>`
  display: inline-block;
  margin-left: 12px;

  svg {
    display: block;
    margin: 0 auto;
    path {
      fill: ${({ $selected }) => ($selected ? 'rgb(0, 61, 92)' : '#687587')};
    }
  }
`;

const ChipWrapper = styled.div`
  border-radius: 16px;

  .MuiButtonBase-root:hover,
  .MuiButtonBase-root:focus {
    background-color: transparent !important;
  }

  .MuiChip-root {
    cursor: pointer;
  }
`;

const StackRow = styled.span`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
`

export const ChipSkeleton = () => {
  return (
    <Skeleton animation="wave" variant="rect" width={100} height={32} style={{ borderRadius: "16px" }} />
  )
}

export const Chips = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const anchor = useRef();
  const { items, loading } = useTabs();

  const handleMoreMenu = () => {
    setOpen(!open);
  };

  const handleChip = (id) => {
    setOpen(false);

    triggerLeftMenu(`/wb/activity/mywork?tabs=${id}`, false, '', undefined);
  };

  const getCurrentTab = () => {
    const url = new URL(window.location.href);
    const tab = Number.isInteger(window.myWorkActiveTab)
      ? window.myWorkActiveTab
      : 2;
    return url.searchParams.has('tabs')
      ? parseInt(url.searchParams.get('tabs'), 10)
      : tab;
  };

  const isSelected = (ids) => {
    if (ids === window.myWorkActiveTab) return true;

    ids = Array.isArray(ids) ? ids : [ids];

    return ids.includes(getCurrentTab());
  };

  const renderLabel = useCallback((item) => {
    return (
      <StackRow>
        <span>{item.label}</span>
        <span style={{ opacity: 0.6 }}>({item.count})</span>
      </StackRow>);
  }, [])

  const renderMoreLabel = () => {
    const selected = items.find((item) => !item.primary && isSelected(item.id));

    if (selected) {
      return renderLabel(selected);
    }
    return t('More');
  };

  if (loading) return (
    <Wrapper>
      <ChipSkeleton />
      <ChipSkeleton />
      <ChipSkeleton />
      <ChipSkeleton />
    </Wrapper>
  )

  const primaryItems = items.filter((item) => item.primary);
  const others = items.filter((item) => !item.primary);

  return (
    <Wrapper>
      {primaryItems
        .map((item) => (
          <Chip
            key={item.id}
            label={renderLabel(item)}
            size="medium"
            icon={item.icon && <IconWrapper $selected={isSelected(item.id)}>{item.icon}</IconWrapper>}
            type={isSelected(item.id) ? 'filtered' : 'filter'}
            onClick={() => handleChip(item.id)}
          />
        ))}
      <div>
        <ChipWrapper onClick={handleMoreMenu}>
          <Chip
            label={renderMoreLabel()}
            size="medium"
            type={isSelected([3, 4, 5, 6]) ? 'filtered' : 'filter'}
            ref={anchor}
            buttonIcon={open ? <ArrowDropUp /> : <ArrowDropDown />}
            buttonProps={{ 'aria-label': 'More options' }}
            onButtonClick={handleMoreMenu}
          />
        </ChipWrapper>
        <Menu
          open={open}
          anchorEl={anchor.current}
          onClose={handleMoreMenu}
          keepMounted
        >
          {others
            .map((item) => (
              <MenuItem
                key={item.id}
                selected={isSelected(item.id)}
                onClick={() => handleChip(item.id)}
              >
                {renderLabel(item)}
              </MenuItem>
            )
            )}
        </Menu>
      </div>
    </Wrapper>
  );
};
